import { useModal } from '@billy/design-system'
import { createContext, ReactElement, ReactNode, useContext, useMemo, useState } from 'react'
import { useParams } from 'react-router'

import { DepositInvoiceDetailsModal } from '@features/invoices/elements/DepositInvoiceDetailsModal'
import { getDepositInvoiceDetailsModalProps } from '@features/invoices/elements/DepositInvoiceDetailsModal/utils/getDepositInvoiceDetailsModalProps'
import { useFetchQuote } from '@features/invoices/hooks/useFetchQuote'
import { DepositInvoiceConfig } from '@features/invoices/types/invoiceDeposit/depositInvoiceConfig'
import { isDocumentTypeQuote } from '@features/invoices/utils/isDocumentTypeQuote'

import { ModalId } from '../../../../../enums/modalId'
import { InvoiceEditorUrlParams } from '../../../types/invoiceEditorUrlParams'
import { InvoiceEditorVariant } from '../enums/invoiceEditorVariant'
import { useFetchSalesDocumentDraft } from '../hooks/useFetchSalesDocumentDraft'
import { useInvoiceEditorDocumentType } from '../hooks/useInvoiceEditorDocumentType'
import { useInvoiceEditorVariant } from '../hooks/useInvoiceEditorVariant'
import { InvoiceEditorQueryParams } from '../types/invoiceEditorQueryParams'
import { areDepositParamsValid } from '../utils/areDepositParamsValid'
import { getQuoteIdFromDraft } from '../utils/invoiceDraft/getQuoteIdFromDraft'
import { useInvoiceEditorQueryParams } from './invoiceEditorQueryParamsContext'

interface ContextState {
  depositConfig?: DepositInvoiceConfig
  editedDepositConfig?: DepositInvoiceConfig
  openDepositInvoiceDetailsModal: () => void
}

const InvoiceEditorDepositModalContext = createContext<ContextState | undefined>(undefined)

interface InvoiceEditorDepositModalProps {
  children?: ReactNode
}

const depositInvoiceDetailsModalId = ModalId.DepositInvoiceDetailsModal

export const InvoiceEditorDepositModalProvider = ({ children }: InvoiceEditorDepositModalProps): ReactElement => {
  const { documentId } = useParams<InvoiceEditorUrlParams>()

  const { documentType, sourceDocumentType } = useInvoiceEditorDocumentType()
  const { invoiceEditorVariant } = useInvoiceEditorVariant()

  const { queryParams, setQueryParams } = useInvoiceEditorQueryParams()

  const isOperatingOnQuote = !!documentId && isDocumentTypeQuote(sourceDocumentType)
  const isOperatingOnDepositInvoiceDraft = invoiceEditorVariant === InvoiceEditorVariant.DepositInvoiceDraftEdit

  const { salesDocumentDraft } = useFetchSalesDocumentDraft(
    documentType,
    isOperatingOnDepositInvoiceDraft ? documentId : undefined,
  )

  const depositDraftQuoteId = isOperatingOnDepositInvoiceDraft ? getQuoteIdFromDraft(salesDocumentDraft) : undefined

  const { quote } = useFetchQuote(isOperatingOnQuote ? documentId : depositDraftQuoteId)

  const depositModalProps = getDepositInvoiceDetailsModalProps(quote)

  const [editedDepositConfig, setEditedDepositConfig] = useState<DepositInvoiceConfig | undefined>(undefined)

  // Edit mode: the draft's own persisted deposit is the source of truth on load - never
  // re-derived from the quote. It's only replaced once the user actively confirms a change.
  const persistedDepositConfig = useMemo(() => {
    const deposit = salesDocumentDraft?.fields.deposit?.value

    return deposit ? { amount: deposit.amount, type: deposit.amountType } : undefined
  }, [salesDocumentDraft])

  const queryParamsDepositConfig = useMemo(() => {
    if (!queryParams || !quote) {
      return undefined
    }

    if (areDepositParamsValid(queryParams, quote) && queryParams.depositType) {
      return { type: queryParams.depositType, amount: Number(queryParams.depositAmount) }
    }

    return undefined
  }, [queryParams, quote])

  const depositConfig = isOperatingOnDepositInvoiceDraft
    ? (editedDepositConfig ?? persistedDepositConfig)
    : queryParamsDepositConfig

  const handleCloseDepositInvoiceDetailsModal = (formData: DepositInvoiceConfig) => {
    if (isOperatingOnDepositInvoiceDraft) {
      setEditedDepositConfig(formData)
    } else {
      setQueryParams((params: InvoiceEditorQueryParams) => ({
        ...params,
        depositAmount: formData.amount,
        depositType: formData.type,
      }))
    }
    closeDepositInvoiceDetailsModal()
  }

  const { open: openDepositInvoiceDetailsModal, close: closeDepositInvoiceDetailsModal } =
    useModal(depositInvoiceDetailsModalId)

  const contextValue = useMemo(
    () => ({
      depositConfig,
      editedDepositConfig,
      openDepositInvoiceDetailsModal,
    }),
    [depositConfig, editedDepositConfig, openDepositInvoiceDetailsModal],
  )

  return (
    <InvoiceEditorDepositModalContext.Provider value={contextValue}>
      {children}
      <DepositInvoiceDetailsModal
        {...depositModalProps}
        id={depositInvoiceDetailsModalId}
        onClose={closeDepositInvoiceDetailsModal}
        onConfirm={handleCloseDepositInvoiceDetailsModal}
        predefinedValues={depositConfig}
      />
    </InvoiceEditorDepositModalContext.Provider>
  )
}

export const useInvoiceEditorDepositModal = () => {
  const context = useContext(InvoiceEditorDepositModalContext)

  if (!context) {
    throw new Error('InvoiceEditorDepositModalProvider is missing in the module!')
  }

  return context
}
